/* eslint-disable eqeqeq */
import { inject, Injectable } from '@angular/core';
import { LenderService } from '@shared/service/lender/lender.service';
import { StatefulValue } from '@workflow/class/stateful-value.class';
import { DecisionStatus } from '@workflow/ecp-shared/enum/enum';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { OaoPageHelper } from '../../helper/oao-page.helper';
import { StatefulService } from '@app/service/stateful.service';

/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OaoOnChangeFunctionsService {
  private readonly statefulService = inject(StatefulService);

  constructor(
    private readonly workflowService: WorkflowService,
    private readonly lenderService: LenderService,
    private readonly flowHelper: OaoPageHelper
  ) {}

  // Flag to tigger Instatouch API call once.
  private isInstatouchTriggered = false;
  /**
   * Disable accounts and service page
   * oao-additional-accounts
   */
  public disableAccountSerivePage = (context: LogicFnContext, pageId: string, bundleValue: boolean): boolean => {
    const enableMidappDecision = typeof bundleValue === 'boolean' ? bundleValue : true;
    if (!enableMidappDecision) return false;
    const decisionStatus = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationStatus').value;
    const hasAppCheckpointPass = this.statefulService.wrap('api:oaoMemberInformation/hasApplicationCheckpointPass').value as boolean;
    return !!(decisionStatus?.toString().toLowerCase() === DecisionStatus.Declined || !hasAppCheckpointPass);
  };
  /**
   * Is OAO Identity page.
   */
  public checkpointCompleted = (context: LogicFnContext): boolean => {
    if (!this.isMidappDecisionEnabled()) return false;
    const page = context.page;
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    const checkpoint_page_id =
      membershipTypeName && membershipTypeName.toLowerCase() === 'business'
        ? 'oao-application-checkpoint-business'
        : 'oao-application-checkpoint';
    const hasApplicationCheckpointPass = this.statefulService.wrap('api:oaoMemberInformation/hasApplicationCheckpointPass')
      .value as boolean;
    const requiredPages = this.workflowService.currentFlow.allPages.find((p) => p.config.id == checkpoint_page_id)?.config?.requiredPages;
    if (!requiredPages) {
      return false;
    }

    const index = requiredPages.findIndex((p) => p == page.config.id);
    return (hasApplicationCheckpointPass && index != -1) || (hasApplicationCheckpointPass && page.id == checkpoint_page_id);
  };

  /** Method to remove first cChar zero(0) to percentage field  */
  public removeFirstCharZero = (context: LogicFnContext): void => {
    if (context.field && context.field.data.value && context.field.data.value.toString().charAt(0) === '0') {
      context.field.data.value =
        parseFloat(context.field.data.value.toString()) == 0 ? `%` : parseFloat(context.field.data.value.toString()) + `%`;
    }
  };

  /**
   * Load BeneficialOwner && authorizedSigners list for business
   *   * @param arrayPath - Path of respective business array
   */
  /** To load Additional Reponsible and Authorized card List */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public updateBSAApplicantDetails = (context: LogicFnContext): void => {
    const ResponsiblePartyCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/responsiblePartyCount'
    ).valueStringOnly;
    if (ResponsiblePartyCount && Number.parseInt(ResponsiblePartyCount) > 0) {
      const lstCount = StatefulValue.arraySize('api:oaoMemberInformation/businessInformation/responsibleParties');
      for (let i = 0; i < lstCount; i++) {
        const percentage = this.statefulService.wrap(
          `api:oaoMemberInformation/businessInformation/responsibleParties/${i}/percentage`
        ).value;
        if (percentage) {
          const objApplicant = this.getOAOApplicantName(`api:oaoMemberInformation/businessInformation/responsibleParties/${i}`);
          this.statefulService.wrap(
            // eslint-disable-next-line max-len
            `api:oaoMemberInformation/businessInformation/bankSecrecyAct/publicOfficeForgeinGovernmentsList/responsibleParties/${i}/applicantName`
          ).value = objApplicant;
        }
      }
    }

    const BeneficialOwnerCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/additionalBeneficialOwnerCount'
    ).valueStringOnly;
    if (BeneficialOwnerCount && Number.parseInt(BeneficialOwnerCount) > 0) {
      const lstCount = StatefulValue.arraySize('api:oaoMemberInformation/businessInformation/additionalBeneficialOwner');
      for (let i = 0; i < lstCount; i++) {
        const objApplicant = this.getOAOApplicantName(`api:oaoMemberInformation/businessInformation/additionalBeneficialOwner/${i}`);
        this.statefulService.wrap(
          // eslint-disable-next-line max-len
          `api:oaoMemberInformation/businessInformation/bankSecrecyAct/publicOfficeForgeinGovernmentsList/beneficialOwner/${i}/applicantName`
        ).value = objApplicant;
      }
    }

    const additionalAuthorizedCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/additionalAuthorizedCount'
    ).valueStringOnly;
    if (additionalAuthorizedCount && Number.parseInt(additionalAuthorizedCount) > 0) {
      const lstCount = StatefulValue.arraySize('api:oaoMemberInformation/businessInformation/additionalAuthorizedSigners');
      for (let i = 0; i < lstCount; i++) {
        const objApplicant = this.getOAOApplicantName(`api:oaoMemberInformation/businessInformation/additionalAuthorizedSigners/${i}`);
        this.statefulService.wrap(
          // eslint-disable-next-line max-len
          `api:oaoMemberInformation/businessInformation/bankSecrecyAct/publicOfficeForgeinGovernmentsList/authorizedSigners/${i}/applicantName`
        ).value = objApplicant;
      }
    }
  };

  /** Method to get appliant full name  */
  public getOAOApplicantName(apiPrimaryRoot: string): string {
    return (
      this.statefulService.wrap(`${apiPrimaryRoot}/firstName`).valueString +
      ' ' +
      this.statefulService.wrap(`${apiPrimaryRoot}/lastName`).valueString
    );
  }

  /** Flag to identify is Midapp Decision Enabled */
  private isMidappDecisionEnabled(): boolean {
    const bundleValue = this.statefulService.wrap('api:oaoMemberInformation/isMidAppDecisionEnabled').value;
    return typeof bundleValue == 'boolean' ? bundleValue : true;
  }

  // Submits Instatouch data when MidAppDecision is Off
  public submitFinalCompareAndFraud = (): void => {
    const midAppDecisionFlag = this.statefulService.wrap('api:oaoMemberInformation/isMidAppDecisionEnabled').value;
    if (!this.isInstatouchTriggered) {
      this.isInstatouchTriggered = true;
      if (!midAppDecisionFlag) {
        this.flowHelper.submitCompareAndFraud();
      }
    }
  };

  // get Notification status
  public getNotificatoinStatus = (): void => {
    this.flowHelper.getNotificatoinStatus();
  };

  public removeOaoCoApplicant = (): void => {
    this.flowHelper.removeOaoApplicantAndContinue();
  };
}
